import i18n from '~/i18n'
import { ClickCutoffAlertIcon } from '~/modules/retailMedia/components/ClickCutoffAlertIcon'
import { ConversionRateCutoffAlertIcon } from '~/modules/retailMedia/components/ConversionRateCutoffAlertIcon'
import { ConversionsCutoffAlertIcon } from '~/modules/retailMedia/components/ConversionsCutoffAlertIcon'
import { ImpressionShareAlertIcon } from '~/modules/retailMedia/components/ImpressionShareAlertIcon'
import { LostIsBudgetAlertIcon } from '~/modules/retailMedia/components/LostIsBudgetAlertIcon'
import { LostIsRankAlertIcon } from '~/modules/retailMedia/components/LostIsRankAlertIcon'
import { RoasMetricCell } from '~/modules/retailMedia/components/RoasMetricCell'

import { buildCardItem, buildCompareValue, buildNtbCards } from './helpers'
import { TitleWithTooltip } from './TitleWithTooltip'

const buildPublisherCard = (
  card: CardItem<TypesPublisherCard> & { enabled?: boolean }
): CardItem<TypesPublisherCard>[] => buildCardItem(card)

const buildPublisherListAllCard = (
  card: CardItem<TypesPublisherListAllCard> & { enabled?: boolean }
): CardItem<TypesPublisherListAllCard>[] => buildCardItem(card)

export const publisherCards: CardsSchema<
  ReportCampaigns,
  TypesPublisherCard
> = ({
  data,
  loading,
  compareData,
  isCompare,
  availableMetrics,
  showClickAlert,
  lookbackMonths,
  ntbOrdersWindow
}) => [
  // Default cards — order matches dashboard reference
  ...buildPublisherCard({
    id: 'card-02',
    label: i18n.t('rm:metrics.impressions'),
    title: <TitleWithTooltip metric="impressions" />,
    value: data?.formatted?.impressions || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.impressions
    ),
    type: 'prints',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-views',
    label: i18n.t('rm:metrics.views'),
    title: <TitleWithTooltip metric="views" />,
    value: data?.formatted?.views || '-',
    compareValue: buildCompareValue(isCompare, compareData?.formatted?.views),
    type: 'views',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-03',
    label: i18n.t('rm:metrics.clicks'),
    title: <TitleWithTooltip metric="clicks" />,
    value: data?.formatted?.clicks || '-',
    compareValue: buildCompareValue(isCompare, compareData?.formatted?.clicks),
    type: 'clicks',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-04',
    label: i18n.t('rm:metrics.ctr'),
    title: <TitleWithTooltip metric="ctr" />,
    value: data?.formatted?.ctr || '-',
    compareValue: buildCompareValue(isCompare, compareData?.formatted?.ctr),
    type: 'ctr',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-05',
    label: i18n.t('rm:metrics.adsRevenue'),
    title: <TitleWithTooltip metric="adsRevenue" />,
    value: data?.formatted?.total_spent || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.total_spent
    ),
    type: 'ads_income',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-conversions',
    label: i18n.t('rm:metrics.conversions'),
    title: (
      <TitleWithTooltip
        metric="conversions"
        alertIcon={<ConversionsCutoffAlertIcon checkCompare />}
      />
    ),
    value: data?.formatted?.conversions || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.conversions
    ),
    type: 'conversions',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-conversions_click',
    label: i18n.t('rm:metrics.conversionsClick'),
    title: (
      <TitleWithTooltip
        metric="conversionsClick"
        alertIcon={showClickAlert && <ClickCutoffAlertIcon />}
      />
    ),
    value: data?.formatted?.conversions_click || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.conversions_click
    ),
    type: 'conversions_click',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-conversion_rate',
    label: i18n.t('rm:metrics.conversion_rate'),
    title: (
      <TitleWithTooltip
        metric="conversion_rate"
        alertIcon={<ConversionRateCutoffAlertIcon checkCompare />}
      />
    ),
    value: data?.formatted?.conversion_rate || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.conversion_rate
    ),
    type: 'conversion_rate',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-conversion_rate_click',
    label: i18n.t('rm:metrics.conversionRateClick'),
    title: (
      <TitleWithTooltip
        metric="conversionRateClick"
        alertIcon={showClickAlert && <ClickCutoffAlertIcon />}
      />
    ),
    value: data?.formatted?.conversion_rate_click || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.conversion_rate_click
    ),
    type: 'conversion_rate_click',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-07',
    label: i18n.t('rm:metrics.sales'),
    title: <TitleWithTooltip metric="sales" />,
    value: data?.formatted?.income || '-',
    compareValue: buildCompareValue(isCompare, compareData?.formatted?.income),
    type: 'income',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-sales_click',
    label: i18n.t('rm:metrics.salesClick'),
    title: (
      <TitleWithTooltip
        metric="salesClick"
        alertIcon={showClickAlert && <ClickCutoffAlertIcon />}
      />
    ),
    value: data?.formatted?.income_click || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.income_click
    ),
    type: 'sales_click',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-06',
    label: i18n.t('rm:metrics.roas'),
    title: <TitleWithTooltip metric="roas" />,
    value: (
      <RoasMetricCell
        metric="roas"
        value={data?.formatted?.roas || '-'}
        raw={data}
      />
    ),
    compareValue: buildCompareValue(
      isCompare,
      compareData && (
        <RoasMetricCell
          metric="roas"
          value={compareData.formatted?.roas}
          raw={compareData}
        />
      )
    ),
    type: 'roas',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-roas_click',
    label: i18n.t('rm:metrics.roasClick'),
    title: (
      <TitleWithTooltip
        metric="roasClick"
        alertIcon={showClickAlert && <ClickCutoffAlertIcon />}
      />
    ),
    value: data?.formatted?.roas_click || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.roas_click
    ),
    type: 'roas_click',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-roas_halo',
    label: i18n.t('rm:metrics.roasHalo'),
    title: <TitleWithTooltip metric="roasHalo" />,
    value: data?.formatted?.roas_halo || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.roas_halo
    ),
    type: 'roas_halo',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-roas_overall',
    label: i18n.t('rm:metrics.roasOverall'),
    title: <TitleWithTooltip metric="roasOverall" />,
    value: (
      <RoasMetricCell
        metric="overall_roas"
        value={data?.formatted?.roas_overall || '-'}
        raw={data}
      />
    ),
    compareValue: buildCompareValue(
      isCompare,
      compareData && (
        <RoasMetricCell
          metric="overall_roas"
          value={compareData.formatted?.roas_overall}
          raw={compareData}
        />
      )
    ),
    type: 'roas_overall',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-halo_orders',
    label: i18n.t('rm:metrics.haloOrders'),
    title: <TitleWithTooltip metric="haloOrders" />,
    value: data?.formatted?.halo_orders || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.halo_orders
    ),
    type: 'halo_orders',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-halo_revenue',
    label: i18n.t('rm:metrics.haloRevenue'),
    title: <TitleWithTooltip metric="haloRevenue" />,
    value: data?.formatted?.halo_revenue || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.halo_revenue
    ),
    type: 'halo_revenue',
    loading
  }),
  ...buildPublisherCard({
    id: 'card-halo_items',
    label: i18n.t('rm:metrics.haloItems'),
    title: <TitleWithTooltip metric="haloItems" />,
    value: data?.formatted?.halo_items || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.halo_items
    ),
    type: 'halo_items',
    loading
  }),
  ...buildPublisherCard({
    enabled: availableMetrics?.impressionShare,
    id: 'card-impression_share',
    label: i18n.t('rm:metrics.impressionShare'),
    title: (
      <TitleWithTooltip
        metric="impressionShare"
        alertIcon={
          data && (
            <ImpressionShareAlertIcon
              value={data.impression_share}
              loading={loading}
            />
          )
        }
      />
    ),
    value: data?.formatted?.impressionShare || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.impressionShare
    ),
    type: 'impression_share',
    loading
  }),
  ...buildPublisherCard({
    enabled: availableMetrics?.lostIsRank,
    id: 'card-lost_is_rank',
    label: i18n.t('rm:metrics.lostIsRank'),
    title: (
      <TitleWithTooltip
        metric="lostIsRank"
        alertIcon={
          data && (
            <LostIsRankAlertIcon value={data.lost_is_rank} loading={loading} />
          )
        }
      />
    ),
    value: data?.formatted?.lostIsRank || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.lostIsRank
    ),
    type: 'lost_is_rank',
    loading
  }),
  ...buildPublisherCard({
    enabled: availableMetrics?.lostIsBudget,
    id: 'card-lost_is_budget',
    label: i18n.t('rm:metrics.lostIsBudget'),
    title: (
      <TitleWithTooltip
        metric="lostIsBudget"
        alertIcon={
          data && (
            <LostIsBudgetAlertIcon
              value={data.lost_is_budget}
              loading={loading}
            />
          )
        }
      />
    ),
    value: data?.formatted?.lostIsBudget || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.lostIsBudget
    ),
    type: 'lost_is_budget',
    loading
  }),
  ...buildNtbCards({
    data,
    compareData,
    isCompare,
    loading,
    availableMetrics,
    lookbackMonths,
    ntbOrdersWindow
  })
]

export const publisherListAllCards: CardsSchema<
  ReportCampaigns,
  TypesPublisherListAllCard
> = ({
  data,
  loading,
  compareData,
  isCompare,
  availableMetrics,
  showClickAlert,
  lookbackMonths,
  ntbOrdersWindow
}) => [
  // Default cards — order matches dashboard reference
  ...buildPublisherListAllCard({
    id: 'card-03',
    label: i18n.t('rm:metrics.impressions'),
    title: <TitleWithTooltip metric="impressions" />,
    value: data?.formatted?.impressions || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.impressions
    ),
    type: 'prints',
    loading,
    isRelativeInterval: true
  }),
  ...buildPublisherListAllCard({
    id: 'card-views',
    label: i18n.t('rm:metrics.views'),
    title: <TitleWithTooltip metric="views" />,
    value: data?.formatted?.views || '-',
    compareValue: buildCompareValue(isCompare, compareData?.formatted?.views),
    type: 'views',
    loading
  }),
  ...buildPublisherListAllCard({
    id: 'card-04',
    label: i18n.t('rm:metrics.clicks'),
    title: <TitleWithTooltip metric="clicks" />,
    value: data?.formatted?.clicks || '-',
    compareValue: buildCompareValue(isCompare, compareData?.formatted?.clicks),
    type: 'clicks',
    loading,
    isRelativeInterval: true
  }),
  ...buildPublisherListAllCard({
    id: 'card-ctr',
    label: i18n.t('rm:metrics.ctr'),
    title: <TitleWithTooltip metric="ctr" />,
    value: data?.formatted?.ctr || 0,
    compareValue: buildCompareValue(isCompare, compareData?.formatted?.ctr),
    type: 'ctr',
    loading
  }),
  ...buildPublisherListAllCard({
    id: 'card-05',
    label: i18n.t('rm:metrics.adsRevenue'),
    title: <TitleWithTooltip metric="adsRevenue" />,
    value: data?.formatted?.total_spent || 0,
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.total_spent
    ),
    type: 'ads_income',
    loading,
    isRelativeInterval: true
  }),
  ...buildPublisherListAllCard({
    id: 'card-conversions',
    label: i18n.t('rm:metrics.conversions'),
    title: (
      <TitleWithTooltip
        metric="conversions"
        alertIcon={<ConversionsCutoffAlertIcon checkCompare />}
      />
    ),
    value: data?.formatted?.conversions || 0,
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.conversions
    ),
    type: 'conversions',
    loading
  }),
  ...buildPublisherListAllCard({
    id: 'card-conversions_click',
    label: i18n.t('rm:metrics.conversionsClick'),
    title: (
      <TitleWithTooltip
        metric="conversionsClick"
        alertIcon={showClickAlert && <ClickCutoffAlertIcon />}
      />
    ),
    value: data?.formatted?.conversions_click || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.conversions_click
    ),
    type: 'conversions_click',
    loading
  }),
  ...buildPublisherListAllCard({
    id: 'card-conversion_rate',
    label: i18n.t('rm:metrics.conversion_rate'),
    title: (
      <TitleWithTooltip
        metric="conversion_rate"
        alertIcon={<ConversionRateCutoffAlertIcon checkCompare />}
      />
    ),
    value: data?.formatted?.conversion_rate || 0,
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.conversion_rate
    ),
    type: 'conversion_rate',
    loading
  }),
  ...buildPublisherListAllCard({
    id: 'card-conversion_rate_click',
    label: i18n.t('rm:metrics.conversionRateClick'),
    title: (
      <TitleWithTooltip
        metric="conversionRateClick"
        alertIcon={showClickAlert && <ClickCutoffAlertIcon />}
      />
    ),
    value: data?.formatted?.conversion_rate_click || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.conversion_rate_click
    ),
    type: 'conversion_rate_click',
    loading
  }),
  ...buildPublisherListAllCard({
    id: 'card-07',
    label: i18n.t('rm:metrics.sales'),
    title: <TitleWithTooltip metric="sales" />,
    value: data?.formatted?.income || 0,
    compareValue: buildCompareValue(isCompare, compareData?.formatted?.income),
    type: 'income',
    loading,
    isRelativeInterval: true
  }),
  ...buildPublisherListAllCard({
    id: 'card-sales_click',
    label: i18n.t('rm:metrics.salesClick'),
    title: (
      <TitleWithTooltip
        metric="salesClick"
        alertIcon={showClickAlert && <ClickCutoffAlertIcon />}
      />
    ),
    value: data?.formatted?.income_click || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.income_click
    ),
    type: 'sales_click',
    loading
  }),
  ...buildPublisherListAllCard({
    id: 'card-roas',
    label: i18n.t('rm:metrics.roas'),
    title: <TitleWithTooltip metric="roas" />,
    value: (
      <RoasMetricCell
        metric="roas"
        value={data?.formatted?.roas || 0}
        raw={data}
      />
    ),
    compareValue: buildCompareValue(
      isCompare,
      compareData && (
        <RoasMetricCell
          metric="roas"
          value={compareData.formatted?.roas}
          raw={compareData}
        />
      )
    ),
    type: 'roas',
    loading
  }),
  ...buildPublisherListAllCard({
    id: 'card-roas_click',
    label: i18n.t('rm:metrics.roasClick'),
    title: (
      <TitleWithTooltip
        metric="roasClick"
        alertIcon={showClickAlert && <ClickCutoffAlertIcon />}
      />
    ),
    value: data?.formatted?.roas_click || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.roas_click
    ),
    type: 'roas_click',
    loading
  }),
  ...buildPublisherListAllCard({
    id: 'card-roas_halo',
    label: i18n.t('rm:metrics.roasHalo'),
    title: <TitleWithTooltip metric="roasHalo" />,
    value: data?.formatted?.roas_halo || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.roas_halo
    ),
    type: 'roas_halo',
    loading
  }),
  ...buildPublisherListAllCard({
    id: 'card-roas_overall',
    label: i18n.t('rm:metrics.roasOverall'),
    title: <TitleWithTooltip metric="roasOverall" />,
    value: (
      <RoasMetricCell
        metric="overall_roas"
        value={data?.formatted?.roas_overall || '-'}
        raw={data}
      />
    ),
    compareValue: buildCompareValue(
      isCompare,
      compareData && (
        <RoasMetricCell
          metric="overall_roas"
          value={compareData.formatted?.roas_overall}
          raw={compareData}
        />
      )
    ),
    type: 'roas_overall',
    loading
  }),
  ...buildPublisherListAllCard({
    id: 'card-halo_orders',
    label: i18n.t('rm:metrics.haloOrders'),
    title: <TitleWithTooltip metric="haloOrders" />,
    value: data?.formatted?.halo_orders || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.halo_orders
    ),
    type: 'halo_orders',
    loading
  }),
  ...buildPublisherListAllCard({
    id: 'card-halo_revenue',
    label: i18n.t('rm:metrics.haloRevenue'),
    title: <TitleWithTooltip metric="haloRevenue" />,
    value: data?.formatted?.halo_revenue || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.halo_revenue
    ),
    type: 'halo_revenue',
    loading
  }),
  ...buildPublisherListAllCard({
    id: 'card-halo_items',
    label: i18n.t('rm:metrics.haloItems'),
    title: <TitleWithTooltip metric="haloItems" />,
    value: data?.formatted?.halo_items || '-',
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.halo_items
    ),
    type: 'halo_items',
    loading
  }),
  ...buildPublisherListAllCard({
    enabled: availableMetrics?.impressionShare,
    id: 'card-impression_share',
    label: i18n.t('rm:metrics.impressionShare'),
    title: (
      <TitleWithTooltip
        metric="impressionShare"
        alertIcon={
          data && (
            <ImpressionShareAlertIcon
              value={data.impression_share}
              loading={loading}
            />
          )
        }
      />
    ),
    value: data?.formatted?.impressionShare || 0,
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.impressionShare
    ),
    type: 'impression_share',
    loading
  }),
  ...buildPublisherListAllCard({
    enabled: availableMetrics?.lostIsRank,
    id: 'card-lost_is_rank',
    label: i18n.t('rm:metrics.lostIsRank'),
    title: (
      <TitleWithTooltip
        metric="lostIsRank"
        alertIcon={
          data && (
            <LostIsRankAlertIcon value={data.lost_is_rank} loading={loading} />
          )
        }
      />
    ),
    value: data?.formatted?.lostIsRank || 0,
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.lostIsRank
    ),
    type: 'lost_is_rank',
    loading
  }),
  ...buildPublisherListAllCard({
    enabled: availableMetrics?.lostIsBudget,
    id: 'card-lost_is_budget',
    label: i18n.t('rm:metrics.lostIsBudget'),
    title: (
      <TitleWithTooltip
        metric="lostIsBudget"
        alertIcon={
          data && (
            <LostIsBudgetAlertIcon
              value={data.lost_is_budget}
              loading={loading}
            />
          )
        }
      />
    ),
    value: data?.formatted?.lostIsBudget || 0,
    compareValue: buildCompareValue(
      isCompare,
      compareData?.formatted?.lostIsBudget
    ),
    type: 'lost_is_budget',
    loading
  }),
  ...buildNtbCards({
    data,
    compareData,
    isCompare,
    loading,
    availableMetrics,
    lookbackMonths,
    ntbOrdersWindow
  })
]
